import { useState } from "react";
import { Loader2, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Popover-based note editor for a single lead row. Isolated from
 * AdminLeads.jsx so the parent doesn't need to manage per-row draft state.
 */
export const LeadNotesEditor = ({ leadId, leadName, notes, onSave }) => {
  const [draft, setDraft] = useState(notes || "");
  const [saving, setSaving] = useState(false);
  const hasNote = Boolean((notes || "").trim());

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(leadId, draft);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover onOpenChange={(open) => open && setDraft(notes || "")}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn("h-8 gap-1.5 px-2 text-xs", hasNote ? "text-[color:var(--navy-950)]" : "text-slate-400")}
          data-testid={`admin-lead-note-trigger-${leadId}`}
        >
          <StickyNote className="h-3.5 w-3.5" />
          {hasNote ? <span className="max-w-[110px] truncate">{notes}</span> : "Add note"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 bg-white" align="start">
        <p className="text-sm font-medium text-[color:var(--navy-950)] mb-2">Status note for {leadName}</p>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. Left voicemail, awaiting documents, scheduled for Tuesday..."
          className="min-h-[100px] text-sm"
          maxLength={2000}
          data-testid={`admin-lead-note-textarea-${leadId}`}
        />
        <div className="mt-3 flex justify-end">
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid={`admin-lead-note-save-${leadId}`}>
            {saving ? (
              <span className="flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving...</span>
            ) : (
              "Save note"
            )}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
